import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useApiMutation } from '@/hooks/useApiMutation';
import { useServiceTypes } from '@/hooks/scheduler/useServiceTypes';
import { useServiceAreas } from '@/hooks/service-areas/useServiceAreas';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerTechnician } from '@/types/scheduler';
import { routeFieldsToParts } from '@/components/organisms/technicians/route-address';

import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { TechnicianForm } from '@/components/organisms/technicians/TechnicianForm';
import {
  buildTechnicianFormValues,
  type TechnicianFormValues,
} from '@/components/organisms/technicians/technician-form-utils';
import { saveTechnicianRelations } from '@/components/organisms/technicians/technician-form-save';

export const Route = createFileRoute('/_dashboard/dashboard/technicians/new')({
  component: NewTechnicianPage,
});

function NewTechnicianPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createdTech, setCreatedTech] = useState<SchedulerTechnician | null>(null);
  const serviceTypesQuery = useServiceTypes();
  const { data: serviceAreas = [], isLoading: areasLoading } = useServiceAreas();

  const createMutation = useApiMutation<SchedulerTechnician, TechnicianFormValues>({
    mutationFn: async (dto) => {
      const technician = await apiPost<SchedulerTechnician>(
        operationsClient,
        schedulerEndpoints.technicians(),
        {
          name: dto.name,
          email: dto.email,
          phone: dto.phone || undefined,
          bio: dto.bio.trim() || undefined,
          capacityPerDay: dto.capacityPerDay,
          capacityPerWeek: dto.capacityPerWeek,
          maxHoursPerDay: dto.maxHoursPerDay,
          maxConsecutiveHours: dto.maxConsecutiveHours,
          minBreakMinutes: dto.minBreakMinutes,
          maxDriveTimeMinutesPerDay: dto.maxDriveTimeMinutesPerDay,
          timezone: dto.timezone,
          isActive: dto.isActive,
          startAddressParts: routeFieldsToParts(dto.startAddress) ?? undefined,
          endAddressParts: routeFieldsToParts(dto.endAddress) ?? undefined,
        },
      );
      await saveTechnicianRelations(technician.id, dto);
      // NOT-294: optionally create the login (invite) in the same flow, so the
      // technician lands in Settings → Users with roles right away.
      if (dto.createLogin) {
        await apiPost(
          operationsClient,
          `${schedulerEndpoints.technicians()}/${technician.id}/invite`,
          { roleIds: dto.roleIds },
        );
      }
      return technician;
    },
    onSuccess: (tech, dto) => {
      queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      toast.success(
        dto.createLogin
          ? 'Technician created — invite sent to set up their login'
          : 'Technician created',
      );
      setCreatedTech(tech);
    },
  });

  function goBack() {
    void navigate({ to: '/dashboard/technicians' });
  }

  return (
    <>
      <TechnicianForm
        mode="create"
        initialValues={buildTechnicianFormValues()}
        submitting={createMutation.isPending}
        onBack={goBack}
        onSubmit={(values) => createMutation.mutate(values)}
        serviceTypes={serviceTypesQuery.data ?? []}
        serviceAreas={serviceAreas}
        loadingOptions={serviceTypesQuery.isLoading || areasLoading}
      />

      <Dialog open={!!createdTech} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Technician Created</DialogTitle>
            <DialogDescription>
              {createdTech?.name} has been added. Would you like to open the technician details?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="w-full sm:w-auto" onClick={goBack}>
              Back to List
            </Button>
            <Button
              className="w-full sm:w-auto"
              onClick={() => {
                if (createdTech) {
                  void navigate({
                    to: '/dashboard/technicians/$id',
                    params: { id: createdTech.id },
                  });
                }
              }}
            >
              Open Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
